import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import { sendSetCurrentOrganization, wsRequest, fetchMyInvites } from '../../api/ws'
import { logout } from '../../api/logout'
import { InvitationsSheet } from '../invitations/InvitationsSheet'
import './OrphanedScreen.css'

type Mode = 'create' | 'accept'

// Full-screen state when the logged-in user has zero org memberships.
// Reached two ways:
//   - A brand-new user who was invited: enrollment deliberately withholds
//     the bootstrap household so they can choose rather than be handed one
//     they'd have to abandon (see enroll.go).
//   - An existing user whose last household was archived, or who was
//     removed from it.
//
// Two recovery paths, and which one leads depends on whether anyone has
// actually invited them:
//   - Accept one or more invitations (multi-select; see InvitationsSheet).
//   - Create a new household, becoming its owner.
// Either resolves the orphaned state — the store's organizations reducer
// flips availableOrganizations to non-empty on the next frame and
// App.tsx's router falls through to HomeScreen.
export function OrphanedScreen() {
  const email = useAppStore((s) => s.email)
  const myInvites = useAppStore((s) => s.myInvites)
  const connectionStatus = useAppStore((s) => s.connectionStatus)

  // Lead with invitations when there are any: someone who was invited and
  // is staring at "No household" should see the way in, not a form.
  const [mode, setMode] = useState<Mode>(() => (myInvites.length > 0 ? 'accept' : 'create'))
  const [modeTouched, setModeTouched] = useState(false)
  const [name, setName] = useState(() => suggestHouseholdName(email))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invitationsLinkPending = useAppStore((s) => s.invitationsLinkPending)
  const setInvitationsLinkPending = useAppStore((s) => s.setInvitationsLinkPending)

  useEffect(() => {
    if (connectionStatus === 'connected') fetchMyInvites()
  }, [connectionStatus])

  // This screen IS the org-less invitee's invitations UI, so a pending
  // "open my invitations" deep-link signal is already honored just by
  // being here. Consume it, or it lingers until the user joins and then
  // App.tsx pops a now-empty invitations sheet on HomeScreen.
  useEffect(() => {
    if (invitationsLinkPending) setInvitationsLinkPending(false)
  }, [invitationsLinkPending, setInvitationsLinkPending])

  // Invitations usually arrive just after this screen mounts (the
  // my_invites push follows the WS handshake). Switch to them on arrival,
  // but never override a choice the user has already made.
  useEffect(() => {
    if (!modeTouched && myInvites.length > 0) setMode('accept')
  }, [myInvites.length, modeTouched])

  async function createOrg() {
    const trimmed = name.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const resp = await wsRequest<{
        organization?: { id: string }
        error?: string
      }>(
        'create_organization',
        { name: trimmed },
        'organization_created',
        { kind: 'organization', action: 'create' },
      )
      if (resp.error || !resp.organization?.id) {
        setError(resp.error ?? 'failed to create household')
        setSubmitting(false)
        return
      }
      // Server made the caller an owner in the same tx. Switch to
      // it immediately so the App.tsx router falls through to the
      // normal HomeScreen on the next render.
      sendSetCurrentOrganization(resp.organization.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitting(false)
    }
  }

  function onLogOut() {
    if (confirm('Log out of Hearth on this device?')) {
      void logout()
    }
  }

  function switchMode(next: Mode) {
    setMode(next)
    setModeTouched(true)
    setError(null)
  }

  const inviteCount = myInvites.length

  return (
    <div className="orphaned">
      <div className="orphaned__card">
        <div className="orphaned__title">No household</div>
        <p className="orphaned__body">
          {inviteCount > 0
            ? "You're not a member of any household yet. Accept an invitation below, or create your own."
            : "You're not a member of any household right now. Create one to keep going."}
        </p>

        <div className="orphaned__tabs" role="tablist">
          <button
            role="tab"
            aria-selected={mode === 'create'}
            className={`orphaned__tab ${mode === 'create' ? 'orphaned__tab--active' : ''}`}
            onClick={() => switchMode('create')}
          >
            Create household
          </button>
          <button
            role="tab"
            aria-selected={mode === 'accept'}
            className={`orphaned__tab ${mode === 'accept' ? 'orphaned__tab--active' : ''}`}
            onClick={() => switchMode('accept')}
          >
            {inviteCount > 0 ? `Invitations (${inviteCount})` : 'Invitations'}
          </button>
        </div>

        {mode === 'create' ? (
          <>
            <label className="orphaned__label">Household name</label>
            <input
              className="orphaned__input"
              autoFocus
              value={name}
              placeholder="e.g. The Smiths"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void createOrg()
              }}
            />
            {error && <div className="orphaned__error">{error}</div>}
            <button
              className="orphaned__primary"
              disabled={submitting || name.trim() === ''}
              onClick={() => void createOrg()}
            >
              {submitting ? 'Creating…' : 'Create household'}
            </button>
          </>
        ) : (
          // Nothing to type. Invitations are found from the address this
          // device is signed in as, so the only interaction is choosing.
          <InvitationsSheet offerMode onCreateOwn={() => switchMode('create')} />
        )}

        <button className="orphaned__logout" onClick={onLogOut}>
          Log Out
        </button>
      </div>
    </div>
  )
}

// Best-effort starter name from the user's email so the field isn't
// empty. "user@example.com" → "user's household". The user can edit
// before submitting; this is just a friendly default.
function suggestHouseholdName(email: string | null): string {
  if (!email) return ''
  const local = email.split('@')[0]
  if (!local) return ''
  return `${local}'s household`
}
